"use client";
import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Image from "next/image";
import { useTranslation } from "react-i18next";

// ─── Assets ─────────────────────────────────────────────────────────────────
const RIGHT_IMG = "https://getgeek-website-assets.b-cdn.net/VisionLeaprightImg.webp";

// ─── Styled Components ──────────────────────────────────────────────────────
const VisionSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#F4F2FB",
  padding: "72px 0",
  [theme.breakpoints.down("sm")]: {
    padding: "48px 0",
  },
}));

const VisionRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "48px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column-reverse",
    gap: "32px",
  },
}));

const LeftContent = styled(Box)(({ theme }) => ({
  flex: "0 0 42%",
  maxWidth: "42%",
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    maxWidth: "100%",
  },
}));

const RightImageWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  flex: "0 0 55%",
  maxWidth: "50%",
  borderRadius: "16px",
  overflow: "hidden",
  lineHeight: 0,
  marginLeft: isRTL ? 0 : "2%",
  marginRight: isRTL ? "3%" : 0,
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    maxWidth: "100%",
    marginLeft: 0,
    marginRight: 0,
  },
}));

const Eyebrow = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "14px",
  fontWeight: 700,
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "#9048FB",
  marginBottom: "16px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
  "&::before": {
    content: '""',
    display: "inline-block",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#9048FB",
  },
}));

const VisionHeading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontWeight: 700,
  fontSize: "35px",
  lineHeight: 1.3,
  color: "#171522",
  marginBottom: "20px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "27px",
  },
}));

const VisionParagraph = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "16px",
  lineHeight: 1.8,
  color: "#5A5568",
  marginBottom: "16px",
}));

// ─── Component ───────────────────────────────────────────────────────────────
const LeapVisionSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  return (
    <VisionSection>
      <Container maxWidth="xl">
        <VisionRow>
          {/* Left Content */}
          <LeftContent>
            <Eyebrow isRTL={isRTL}>{t("leapVisionSection.eyebrow")}</Eyebrow>

            <VisionHeading variant="h2" isRTL={isRTL}>
              {t("leapVisionSection.heading")}
            </VisionHeading>

            <VisionParagraph isRTL={isRTL}>
              {t("leapVisionSection.paragraphOne")}
            </VisionParagraph>

            <VisionParagraph isRTL={isRTL}>
              {t("leapVisionSection.paragraphTwo")}
            </VisionParagraph>
          </LeftContent>

          {/* Right Image */}
          <RightImageWrapper isRTL={isRTL}>
            <Image
              src={RIGHT_IMG}
              alt="Saudi Vision 2030"
              width={760}
              height={480}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: "cover",
              }}
            />
          </RightImageWrapper>
        </VisionRow>
      </Container>
    </VisionSection>
  );
};

export default LeapVisionSection;